import moment from "moment";

import { PredefinedRuleSet } from "@/Appointments/Api/Dto/Responses/PredefinedRuleSet";
import { PredefinedRuleSetModel } from "@/Appointments/Models/Scheduling/PredefinedRuleSetModel";
import { TemplateInfo } from "@/Appointments/Models/Scheduling/TemplateInfo";
import { TemplateLight } from "@/Appointments/Models/Scheduling/TemplateLight";
import { CalendarSlotV2, WorkDayV2 } from "@/Appointments/Models/Scheduling/WorkDayV2";
import { SchedulingStore } from "@/Appointments/Stores/SchedulingStore";
import { ServiceDefinitionModelEmployee } from "@/Common/Models/Services/ServiceDefinitionModel";
import { SchedulingTabViews } from "@/Common/Models/UI/Enums";
import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { BaseServerOperationHandler } from "@/Common/Stores/Handlers/Server/BaseServerOperationHandler";

class SchedulingResponseHandler extends BaseServerOperationHandler {
    constructor(action: BaseAction) {
        super(action);

        this.RegisterOperation(ServerOperations.Server.Myle_Scheduling_GetRuleset, this.getRuleset, SubServerOperations.Scheduling.LoadRuleset);
        this.RegisterOperation(ServerOperations.Server.Myle_Scheduling_LoadLightTemplate, this.loadLightTemplate);
        this.RegisterOperation(ServerOperations.Server.Myle_Scheduling_GetTemplateInfo, this.getTemplateInfo);
        this.RegisterOperation(ServerOperations.Server.Myle_Scheduling_DeleteTemplate, this.deleteTemplate);
        this.RegisterOperation(ServerOperations.Server.Myle_Scheduling_SavePredefinedTemplate, this.updateTemplateAfterSaveOrUpdate);
        this.RegisterOperation(ServerOperations.Server.Myle_Scheduling_UpdatePredefinedTemplate, this.updateTemplateAfterSaveOrUpdate);
    }

    private getRuleset(response: ServerEvent, store: SchedulingStore): void {
        var rulesets = response.Data.Rulesets;

        if (rulesets && rulesets.length > 0) {
            var rulesetModel = PredefinedRuleSetModel.New();
            rulesetModel.Model = rulesets[0];
            var comboServiceList = rulesetModel.ToComboServiceList();
            store.OpenScheduleTabModel = store.OpenScheduleTabModel.set("SelectedRuleset", rulesetModel);
            store.OpenScheduleTabModel = store.OpenScheduleTabModel.set("SelectedComboServiceList", comboServiceList.toImmutable());
        }
    }

    private loadLightTemplate(response: ServerEvent, store: SchedulingStore): void {
        var templateList: TemplateLight[] = response.Data["TemplateList"];

        if (templateList != null && templateList.length > 0) {
            store.TemplateTabModel = store.TemplateTabModel.set("TemplateList", templateList.toImmutable());
        }
        else {
            store.TemplateTabModel = store.TemplateTabModel.set("TemplateList", [].toImmutable());
        }
    }

    private getTemplateInfo(response: ServerEvent, store: SchedulingStore): void {
        let templateInfo: TemplateInfo[] = response.Data["TemplateInfo"];
        let ruleSets = Immutable.List<PredefinedRuleSet>(response.Data["RulesetInfo"]);

        let workDays = [];
        let serviceDefinitionIds = [];

        let workDay = new WorkDayV2();
        workDay.date = moment().startOf("day").toDate();
        workDay.calendarSlots = [];
        workDay.HasRuleSet = false;

        let employeeList: ServiceDefinitionModelEmployee[] = [];

        if (templateInfo != null && templateInfo.length > 0) {
            templateInfo.forEach((item: TemplateInfo) => {
                if (sessionStore.userLanguageCode === "fr")
                    item.Name = item.NameFR;
                else if (sessionStore.userLanguageCode === "en")
                    item.Name = item.NameEN;
                else if (sessionStore.userLanguageCode === "es-MX")
                    item.Name = item.NameEN;

                workDay.calendarSlots.push(CalendarSlotV2.fromTemplateInfo(item));
            });

            serviceDefinitionIds = templateInfo.map(n => n.ServiceDefinitionId);

            store.TemplateTabModel.set("IsRuleSetTemplate", false);
        }
        if (ruleSets?.size > 0) {
            const editedRuleSets = ruleSets.toArray();
            for (let ruleset of editedRuleSets) {
                if (!ruleset)
                    continue;

                ruleset.StartDate = moment(ruleset.StartDate)
                    .setToday()
                    .toISOString();

                ruleset.EndDate = moment(ruleset.EndDate)
                    .setToday()
                    .toISOString();

                workDay.calendarSlots.push(CalendarSlotV2.FromPredefinedRuleSet(ruleset));
            }

            workDay.HasRuleSet = editedRuleSets.length > 0;

            for (let ruleset of editedRuleSets)
                serviceDefinitionIds = [...serviceDefinitionIds, ...ruleset.PredefinedRuleSetItems.map(i => i.ServiceDefinitionId)];

            store.TemplateTabModel.set("IsRuleSetTemplate", true);
        }

        serviceDefinitionIds = serviceDefinitionIds.distinct();
        const serviceDefList = cacheStore.ServiceDefinitionCache.filter(
            serviceDef => serviceDefinitionIds.some(id => id == serviceDef.Model.Id)
        );

        serviceDefList.forEach(serviceDef => {
            employeeList = [...employeeList, ...serviceDef.Employees.map(x => x.clone())];
        });

        employeeList = employeeList
            .distinctByProperty(e => e.ContactFacetId)
            .sortByProperty(e => e.NameWithPrefix);

        store.TemplateTabModel = store.TemplateTabModel.set("IntervenorsList", employeeList.toImmutable());

        workDay.calendarSlots = workDay.calendarSlots.sortByProperty(n => n.fromRaw);
        workDays.push(workDay);

        store.TemplateTabModel = store.TemplateTabModel.set("SelectedTemplateWorkDay", workDays.toImmutable());
    }

    public deleteTemplate(response: ServerEvent, store: SchedulingStore): void {
        var success: boolean = response.Data["Success"];

        if (success) {
            store.TemplateTabModel = store.TemplateTabModel.set("SelectedTemplateName", "");
            store.TemplateTabModel = store.TemplateTabModel.set("SelectedTemplateId", "");
            store.TemplateTabModel = store.TemplateTabModel.set("SelectedTemplateWorkDay", [].toImmutable());
        }

        store.schedulingAction.getTemplateLight(store.TemplateTabModel.get("SearchText"));
    }

    public updateTemplateAfterSaveOrUpdate(response: ServerEvent, store: SchedulingStore): void {
        let success = response.Data["Success"];

        let name = response.Data["Name"];
        let templateId = response.Data["TemplateId"];

        if (success) {
            store.SchedulingTabModel = store.SchedulingTabModel.set("CurrentView", SchedulingTabViews.MainTemplate);

            store.TemplateTabModel = store.TemplateTabModel.set("SelectedTemplateName", name);
            store.TemplateTabModel = store.TemplateTabModel.set("SelectedTemplateId", templateId);

            store.schedulingAction.getTemplateInfo(templateId);
        }
    }
}

export { SchedulingResponseHandler };
